import React, { useState, useEffect } from 'react';
import { Link, useOutletContext, Navigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { setLoggingIn } from '../services/api';
import { setNetworkLoggingIn } from '../services/networkApi';
import Footer from '../components/Footer';

const Login = () => {
  const context = useOutletContext();
  const { login, isAuthenticated, user } = context || {};
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState('');
  const [loading, setLoading] = useState(false);

  // Se l'utente è già loggato, reindirizza alla home
  if (isAuthenticated && user) {
    return <Navigate to="/home" replace />;
  }
  const [showLogs, setShowLogs] = useState(false);

  // Debug del context
  //useEffect(() => {
  //  console.log('🔍 Context available:', !!context);
  //  console.log('🔍 Login function available:', typeof login);
  //  console.log('🔍 Full context:', context);
  //}, [context, login]);

  // Error handler globale
  useEffect(() => {
    const handleError = (event) => {
      console.error('🚨 Global error caught:', event.error);
      console.error('🚨 Error message:', event.message);
      console.error('🚨 Error filename:', event.filename);
      console.error('🚨 Error line:', event.lineno);
    };

    const handleUnhandledRejection = (event) => {
      console.error('🚨 Unhandled promise rejection:', event.reason);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Controlla se c'è un messaggio salvato dall'interceptor
  React.useEffect(() => {
    const savedMessage = localStorage.getItem('loginMessage');
    if (savedMessage) {
      try {
        const messageData = JSON.parse(savedMessage);
        setError(messageData.message);
        setErrorType(messageData.type);
        localStorage.removeItem('loginMessage'); // Rimuovi il messaggio dopo averlo mostrato
      } catch (e) {
        console.error('Errore nel parsing del messaggio salvato:', e);
      }
    }
  }, []);

  // Funzione per loggare nel localStorage
  const persistentLog = (message, data = null) => {
    const timestamp = new Date().toISOString();
    const logEntry = `${timestamp}: ${message}${data ? ` - ${JSON.stringify(data)}` : ''}`;
    console.log(logEntry);
    
    try {
      const existingLogs = localStorage.getItem('login_debug_logs') || '';
      localStorage.setItem('login_debug_logs', existingLogs + '\n' + logEntry);
    } catch (e) {
      console.error('Failed to save log to localStorage:', e);
    }
  };

  // Funzione per mostrare/nascondere i log persistenti
  const toggleLogs = () => {
    setShowLogs(!showLogs);
  };

  // Funzione per ottenere i log
  const getLogs = () => {
    try {
      return localStorage.getItem('login_debug_logs') || 'Nessun log trovato';
    } catch (e) {
      return 'Errore nel recuperare i log: ' + e.message;
    }
  };

  // Funzione per copiare i log
  const copyLogs = () => {
    try {
      const logs = getLogs();
      navigator.clipboard.writeText(logs).then(() => {
        alert('Log copiati negli appunti!');
      }).catch(() => {
        // Fallback per browser più vecchi
        const textArea = document.createElement('textarea');
        textArea.value = logs;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        alert('Log copiati negli appunti!');
      });
    } catch (e) {
      alert('Errore nel copiare i log: ' + e.message);
    }
  };

  const handleSubmit = async (e) => {
    try {
      persistentLog('🔄 Login attempt started');
      
      if (e) {
        e.preventDefault();
        e.stopPropagation();
        persistentLog('🔄 preventDefault called');
      }
      
      if (!username || !password) {
        persistentLog('❌ Validation failed');
        setError('Inserisci nome utente e password');
        setErrorType('validation');
        return;
      }

      persistentLog('🔄 Setting loading state');
      setLoading(true);
      setError('');
      setErrorType('');

      // Disabilita logout automatico PRIMA della chiamata API
      persistentLog('🔄 Disabilitando logout automatico prima della chiamata API');
      setLoggingIn(true);
      setNetworkLoggingIn(true);

      persistentLog('🔄 About to call API');
      
      // Wrap API call in try-catch separato
      let response;
      try {
        response = await apiService.login({ username, password });
        persistentLog('✅ API call successful', { hasUser: !!response.user, hasToken: !!response.token });
      } catch (apiError) {
        persistentLog('❌ API call failed', { error: apiError.message, status: apiError.response?.status });
        // Riabilita logout automatico in caso di errore API
        setLoggingIn(false);
        setNetworkLoggingIn(false);
        throw apiError;
      }
      
      persistentLog('🔄 About to call context login');
      
      // Wrap context call in try-catch separato
      try {
        if (typeof login !== 'function') {
          throw new Error('Login function not available');
        }
        persistentLog('🔄 Calling login function');
        login(response.user, response.token);
        persistentLog('✅ Context login completed');
      } catch (contextError) {
        persistentLog('❌ Context login failed', { error: contextError.message, stack: contextError.stack });
        throw contextError;
      }
      
    } catch (error) {
      persistentLog('❌ Login process error', { 
        message: error.message, 
        stack: error.stack,
        responseStatus: error.response?.status,
        responseData: error.response?.data
      });
      
      // Gestione specifica per errori di nodo rete inattivo
      if (error.response?.data?.codice === 'NODO_RETE_INATTIVO') {
        setError(error.response.data.message || 'Il tuo account non ha nodi rete attivi.');
        setErrorType('nodo_rete_inattivo');
      } else {
        // Gestione degli altri errori
        let errorMessage = 'Credenziali non valide';
        if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.error) {
          errorMessage = error.error;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        persistentLog('🔄 Setting error message', { errorMessage });
        setError(errorMessage);
        setErrorType('credentials');
      }
    } finally {
      persistentLog('🔄 Setting loading to false');
      setLoading(false);
      
      // Riabilita logout automatico dopo un breve delay
      setTimeout(() => {
        persistentLog('🔄 Riabilitando logout automatico...');
        setLoggingIn(false);
        setNetworkLoggingIn(false);
      }, 1000);
    }
  };

  // Funzione per ottenere la classe CSS basata sul tipo di errore
  const getErrorClassName = () => {
    switch (errorType) {
      case 'nodo_rete_inattivo':
        return 'bg-orange-100 border border-orange-400 text-orange-700';
      case 'credentials':
        return 'bg-red-100 border border-red-400 text-red-700';
      default:
        return 'bg-red-100 border border-red-400 text-red-700';
    }
  };

  // Funzione per ottenere l'icona basata sul tipo di errore
  const getErrorIcon = () => {
    switch (errorType) {
      case 'nodo_rete_inattivo':
        return (
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="flex flex-col items-center pt-10 pb-6">
            <div className="mb-3">
              <img src="/logo.png" alt="Logo Ayvens" className="h-14" />
            </div>
          </div>
          
          <div className="px-10 pb-10">
            {error && (
              <div className={`${getErrorClassName()} px-4 py-3 rounded-md relative mb-6 flex items-start`} role="alert">
                {getErrorIcon()}
                <div className="flex-1">
                  <span className="block sm:inline font-medium">
                    {errorType === 'nodo_rete_inattivo' ? 'Accesso Limitato' : 'Errore di Accesso'}
                  </span>
                  <p className="text-sm mt-1">{error}</p>
                  {errorType === 'nodo_rete_inattivo' && (
                    <p className="text-sm mt-2 font-medium">
                      Contatta l'amministratore per riattivare il tuo account.
                    </p>
                  )}
                </div>
              </div>
            )}
            
            <div className="space-y-6">
              <div>
                <input
                  type="text"
                  placeholder="Nome utente"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={loading}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSubmit(e);
                    }
                  }}
                  className="w-full px-4 py-3 bg-gray-100 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white transition-colors"
                />
              </div>
              
              <div>
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSubmit(e);
                    }
                  }}
                  className="w-full px-4 py-3 bg-gray-100 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white transition-colors"
                />
              </div>
              
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading || errorType === 'nodo_rete_inattivo'}
                className={`w-full py-3 px-4 rounded-md font-medium text-white transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  loading || errorType === 'nodo_rete_inattivo'
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {loading ? 'Accesso in corso...' : 
                 errorType === 'nodo_rete_inattivo' ? 'Accesso Bloccato' : 
                 'Accedi'}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Login; 